// Pure row-selection helpers for the KPI scorecards. Plain JS (no JSX) so the
// node test runner can import it directly.

export const shouldOmit = (v) => v == null || v === '' || v === 'N/A';

export const toNumber = (v) => {
  // Guard null/undefined first: `Number(null)` is 0 and `Number.isFinite(0)` is
  // true, so without this check a missing field would read as 0.
  if (v == null) return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

export function rowDimValue(r, field, dimAliases) {
  const keys = dimAliases?.[field];
  const keyList = Array.isArray(keys) && keys.length > 0 ? keys : [field];
  for (const k of keyList) {
    const v = r?.[k];
    if (v == null) continue;
    return v;
  }
  return null;
}

export function dimEq(a, b) {
  if (a == null || b == null) return a === b;
  if (typeof a === 'string' && typeof b === 'string') {
    return a.trim().toLowerCase() === b.trim().toLowerCase();
  }
  return a === b;
}

/**
 * All scorecard rows that match chart dims (multi-select + strict fields).
 * Wildcard dims are not enforced on the row (same as matchSingleRow pool before aggregate pick).
 */
export function rowsMatchingDims(rows, dims, dimAliases) {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  if (!dims) return rows.slice();

  const normalized = {};
  for (const [k, v] of Object.entries(dims)) {
    const single = Array.isArray(v) ? (v.length === 1 ? v[0] : null) : v;
    normalized[k] = single;
  }

  let pool = rows;

  for (const [k, v] of Object.entries(dims)) {
    if (!Array.isArray(v) || v.length <= 1) continue;
    const picks = v.filter((x) => !shouldOmit(x));
    if (picks.length === 0) continue;
    pool = pool.filter((r) => {
      const rv = rowDimValue(r, k, dimAliases);
      if (rv == null) return true;
      return picks.some((want) => dimEq(rv, want));
    });
  }
  if (pool.length === 0) return [];

  const strictPass = (r) => {
    for (const [field, want] of Object.entries(normalized)) {
      if (shouldOmit(want)) continue;
      const rowVal = rowDimValue(r, field, dimAliases);
      if (rowVal == null) continue;
      if (!dimEq(rowVal, want)) return false;
    }
    return true;
  };

  return pool.filter(strictPass);
}

/**
 * Pick a single row from a metric's `rows` array using user-selected dimensions.
 * Single-select / "All" path. Wildcard dims prefer the backend aggregate row
 * (dim = null). Returns null when nothing matches.
 */
export function matchSingleRow(rows, dims, dimAliases) {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  if (!dims) return rows[0] || null;

  const wildcardFields = [];
  for (const [k, v] of Object.entries(dims)) {
    const single = Array.isArray(v) ? (v.length === 1 ? v[0] : null) : v;
    if (shouldOmit(single)) wildcardFields.push(k);
  }

  const pool = rowsMatchingDims(rows, dims, dimAliases);
  if (pool.length === 0) return null;
  if (wildcardFields.length === 0) return pool[0] || null;

  const score = (r) => wildcardFields.reduce(
    (n, f) => n + (rowDimValue(r, f, dimAliases) == null ? 1 : 0), 0
  );

  let best = pool[0];
  let bestScore = score(best);
  for (let i = 1; i < pool.length; i++) {
    const s = score(pool[i]);
    if (s > bestScore) { best = pool[i]; bestScore = s; }
  }
  return best || null;
}

// ---------- multi-select pick ----------

/** Dims whose selection holds more than one concrete value. */
export function multiDimKeys(dims) {
  if (!dims) return [];
  return Object.entries(dims)
    .filter(([, v]) => Array.isArray(v) && v.filter((x) => !shouldOmit(x)).length > 1)
    .map(([k]) => k);
}

const GH_RE = /^(gh|greenhouse|green house|hothouse)$/i;

const rowIsConventional = (r, dimAliases) => {
  const t = rowDimValue(r, 'type', dimAliases);
  return t == null || dimEq(t, 'Conventional');
};

const rowIsGreenhouse = (r, dimAliases) => {
  const e = rowDimValue(r, 'environment', dimAliases);
  return typeof e === 'string' && GH_RE.test(e.trim());
};

const DIM_WORDS = { origin: 'origins', destination: 'terminals' };

/**
 * Choose one row when one or more dims hold several selections.
 *
 * Order: highest signed % (pctKey) -> highest Conventional value (valueKey)
 * -> highest Greenhouse value -> highest value of any candidate -> aggregate
 * row ("All selected") -> nothing. Only concrete rows (every multi dim set and
 * inside the selection) are candidates; backend aggregate rows are the fallback.
 *
 * @returns {{row: object|null, label: string, title: string, basis: string,
 *   count: number, dimWord: string}}
 */
export function pickRowForMultiSelect(rows, dims, opts = {}) {
  const { dimAliases, pctKey, valueKey = 'value' } = opts;
  const keys = multiDimKeys(dims);
  const pool = rowsMatchingDims(rows, dims, dimAliases);
  const dimWord = DIM_WORDS[keys[0]] || `${keys[0] || 'items'}`;
  const count = keys.length
    ? dims[keys[0]].filter((x) => !shouldOmit(x)).length
    : 0;
  const result = (row, label, basis) => {
    const title = row
      ? basis === 'aggregate'
        ? `Showing all ${count} selected ${dimWord}`
        : basis === 'partial'
          ? `Showing ${label} (partial aggregate across ${count} selected ${dimWord})`
          : `Showing ${label}: ${BASIS_TEXT[basis]} of ${count} selected ${dimWord}`
      : '';
    return { row, label, title, basis, count, dimWord };
  };

  const concrete = [];
  const aggregates = [];
  pool.forEach((r, index) => {
    const isConcrete = keys.length > 0 && keys.every((k) => {
      const v = rowDimValue(r, k, dimAliases);
      return v != null && !shouldOmit(v);
    });
    const entry = {
      r,
      index,
      label: keys.map((k) => rowDimValue(r, k, dimAliases)).filter((v) => v != null).join(' · '),
      pct: pctKey ? toNumber(r[pctKey]) : null,
      value: toNumber(r[valueKey]),
      conv: rowIsConventional(r, dimAliases),
      nulls: keys.filter((k) => rowDimValue(r, k, dimAliases) == null).length,
    };
    (isConcrete ? concrete : aggregates).push(entry);
  });

  // Prefer the aggregate covering every multi dim (most null dims); ties keep order.
  const bestAggregate = (list) => list.reduce((a, b) => (b.nulls > a.nulls ? b : a));

  const best = (list, primary) => {
    let top = null;
    for (const e of list) {
      if (top == null) { top = e; continue; }
      const dp = primary(e) - primary(top);
      if (dp !== 0) { if (dp > 0) top = e; continue; }
      if (pctKey && e.pct != null && top.pct != null && e.pct !== top.pct) {
        if (e.pct > top.pct) top = e;
        continue;
      }
      const ev = e.value ?? -Infinity;
      const tv = top.value ?? -Infinity;
      if (ev !== tv) { if (ev > tv) top = e; continue; }
      if (e.conv !== top.conv) { if (e.conv) top = e; continue; }
      const cmp = e.label.localeCompare(top.label);
      if (cmp !== 0) { if (cmp < 0) top = e; continue; }
      if (e.index < top.index) top = e;
    }
    return top;
  };

  // 1. Highest signed %.
  const withPct = concrete.filter((e) => e.pct != null);
  if (withPct.length) {
    const top = best(withPct, (e) => e.pct);
    return result(top.r, top.label, 'pct');
  }
  // 1b. No concrete % but an aggregate carries one: keep the % headline.
  if (pctKey) {
    const aggPct = aggregates.filter((e) => e.pct != null);
    if (aggPct.length) {
      const top = bestAggregate(aggPct);
      return result(top.r, top.nulls === keys.length ? 'All selected' : top.label, top.nulls === keys.length ? 'aggregate' : 'partial');
    }
  }
  // 2. Highest Conventional value.
  const conv = concrete.filter((e) => e.conv && e.value != null);
  if (conv.length) {
    const top = best(conv, (e) => e.value);
    return result(top.r, top.label, 'conventional');
  }
  // 3. Highest Greenhouse value.
  const gh = concrete.filter((e) => rowIsGreenhouse(e.r, dimAliases) && e.value != null);
  if (gh.length) {
    const top = best(gh, (e) => e.value);
    return result(top.r, top.label, 'gh');
  }
  // 4. Highest value of any candidate.
  const anyVal = concrete.filter((e) => e.value != null);
  if (anyVal.length) {
    const top = best(anyVal, (e) => e.value);
    return result(top.r, top.label, 'value');
  }
  // 5. Aggregate row.
  if (aggregates.length) {
    const top = bestAggregate(aggregates);
    return result(top.r, top.nulls === keys.length ? 'All selected' : top.label, top.nulls === keys.length ? 'aggregate' : 'partial');
  }
  return result(null, '', 'none');
}

const BASIS_TEXT = {
  pct: 'highest % change',
  conventional: 'highest Conventional price',
  gh: 'highest GH price',
  value: 'highest price',
};

/**
 * Matching rows with backend aggregate (null multi-dim) rows removed. Returns an
 * empty list when no concrete row exists, so callers hide the tile.
 */
export function concreteRowsMatchingDims(rows, dims, dimAliases) {
  const pool = rowsMatchingDims(rows, dims, dimAliases);
  const keys = multiDimKeys(dims);
  if (keys.length === 0) return pool;
  const concrete = pool.filter((r) => keys.every((k) => {
    const v = rowDimValue(r, k, dimAliases);
    return v != null && !shouldOmit(v);
  }));
  return concrete;
}
